import React, { useEffect, useMemo, useState, Suspense, lazy } from "react";
import {
  HashRouter,
  Routes,
  Route,
  useNavigate,
  useParams,
  Navigate,
  useLocation,
} from "react-router-dom";
import { useStore } from "./store/useStore";
import { QuoteMode, QuoteStatus } from "./types";
import { Loader2, LayoutGrid, Clock } from "lucide-react";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { performanceMonitor } from "./utils/performance";
import { isFeatureEnabled } from "./config/environment";
import { FeedbackButton } from "./components/FeedbackButton";
import { AuthWarningBanner } from "./components/AuthWarningBanner";
import { consumeInvoiceManagerResumePayload } from "./services/integrations/resumePayload";

// Lazy load route components for code-splitting
const Dashboard = lazy(() =>
  import("./components/Dashboard").then((module) => ({ default: module.Dashboard }))
);
const QuoteEstimator = lazy(() =>
  import("./components/QuoteEstimator").then((module) => ({ default: module.QuoteEstimator }))
);
const ProjectManager = lazy(() =>
  import("./components/ProjectManager").then((module) => ({ default: module.ProjectManager }))
);
const TimeTracker = lazy(() =>
  import("./components/TimeTracker").then((module) => ({ default: module.TimeTracker }))
);
const PriceListManager = lazy(() =>
  import("./components/PriceListManager").then((module) => ({ default: module.PriceListManager }))
);
const QuickBooksManager = lazy(() =>
  import("./components/qbo/QuickBooksManager").then((module) => ({
    default: module.QuickBooksManager,
  }))
);
const Microsoft365Panel = lazy(() =>
  import("./components/Microsoft365Panel").then((module) => ({ default: module.Microsoft365Panel }))
);
const EULAPage = lazy(() =>
  import("./components/pages/EULAPage").then((module) => ({ default: module.EULAPage }))
);
const PrivacyPolicyPage = lazy(() =>
  import("./components/pages/PrivacyPolicyPage").then((module) => ({
    default: module.PrivacyPolicyPage,
  }))
);
const Marketing = lazy(() =>
  import("./src/pages/Marketing").then((module) => ({ default: module.Marketing }))
);
const CampaignDetail = lazy(() =>
  import("./src/pages/marketing/CampaignDetail").then((module) => ({ default: module.CampaignDetail }))
);
const HeraldInbox = lazy(() =>
  import("./src/pages/herald/index").then((module) => ({ default: module.HeraldInbox }))
);
const Scout = lazy(() =>
  import("./src/pages/Scout").then((module) => ({ default: module.Scout }))
);
const ScoutTest = lazy(() =>
  import("./src/pages/ScoutTest").then((module) => ({ default: module.ScoutTest }))
);
const ScoutAnalytics = lazy(() =>
  import("./src/pages/scout/Analytics").then((module) => ({ default: module.Analytics }))
);
const EmailTemplates = lazy(() =>
  import("./src/pages/settings/EmailTemplates").then((module) => ({ default: module.EmailTemplates }))
);
const EmailQueue = lazy(() =>
  import("./src/pages/settings/EmailQueue").then((module) => ({ default: module.EmailQueue }))
);
const ForgeTemplates = lazy(() =>
  import("./src/pages/settings/ForgeTemplates").then((module) => ({ default: module.ForgeTemplates }))
);
const ForgeDocuments = lazy(() =>
  import("./src/pages/ForgeDocuments").then((module) => ({ default: module.ForgeDocuments }))
);
const AgentInbox = lazy(() =>
  import("./src/pages/AgentInbox").then((module) => ({ default: module.AgentInbox }))
);

// Loading fallback component - Industrial SCADA style
const RouteLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-[var(--app-bg)] flex-col">
    <div className="relative">
      <div className="w-16 h-16 rounded-full border-4 border-[color:var(--border)]" />
      <div className="absolute inset-0 w-16 h-16 rounded-full border-4 border-oler-blue border-t-transparent animate-spin" />
    </div>
    <h2 className="text-lg font-bold text-[color:var(--text)] mt-6 tracking-wider uppercase">
      Loading...
    </h2>
    <p className="text-dim text-sm mt-2">Initializing system</p>
  </div>
);

// --- ROUTE WRAPPERS ---

const QuoteRoute = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showPriceList, setShowPriceList] = useState(false);

  // Use selectors to prevent unnecessary re-renders
  const quotes = useStore((state) => state.quotes);
  const updateQuote = useStore((state) => state.updateQuote);
  const addKit = useStore((state) => state.addKit);
  const deleteKit = useStore((state) => state.deleteKit);
  const priceList = useStore((state) => state.priceList);
  const termsTemplates = useStore((state) => state.termsTemplates);
  const addTermsTemplate = useStore((state) => state.addTermsTemplate);
  const reviseQuote = useStore((state) => state.reviseQuote);
  const mergePriceList = useStore((state) => state.mergePriceList);
  const companyInfo = useStore((state) => state.companyInfo);
  const timeEntries = useStore((state) => state.timeEntries);
  const kits = useStore((state) => state.kits);
  const updatePriceList = useStore((state) => state.updatePriceList);

  // Find quote from quotes array
  const quote = quotes.find((q) => q.id === id);

  // Fix: Use separate effects and avoid full 'store' dependency to prevent infinite loops
  useEffect(() => {
    if (id) {
      // Use getState() to set active ID without triggering this effect again due to store object reference change
      useStore.getState().setActiveQuoteId(id);
    }
  }, [id]);

  useEffect(() => {
    // Redirect to Project view if Won
    if (quote && quote.status === QuoteStatus.WON) {
      navigate(`/project/${quote.id}`, { replace: true });
    }
  }, [quote?.status, quote?.id, navigate]);

  if (!quote)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--app-bg)]">
        <div className="text-center">
          <div className="text-dim text-sm uppercase tracking-wider">Quote not found</div>
        </div>
      </div>
    );

  return (
    <>
      <ErrorBoundary>
        <Suspense fallback={<RouteLoader />}>
          <div data-testid="quote-estimator-view">
            <QuoteEstimator
              quote={quote}
              onUpdateQuote={updateQuote}
              onBack={() => navigate("/")}
              allQuotes={quotes}
              allTimeEntries={timeEntries}
              kits={kits}
              onSaveKit={addKit}
              onDeleteKit={deleteKit}
              priceList={priceList}
              onOpenPriceList={() => setShowPriceList(true)}
              termsTemplates={termsTemplates}
              onSaveTermTemplate={addTermsTemplate}
              onRevise={reviseQuote}
              onMergePriceList={mergePriceList}
              companyInfo={companyInfo}
            />
          </div>
        </Suspense>
      </ErrorBoundary>
      {showPriceList && (
        <Suspense fallback={<RouteLoader />}>
          <PriceListManager
            priceList={priceList}
            onUpdateList={updatePriceList}
            onMergeList={mergePriceList}
            onClose={() => setShowPriceList(false)}
          />
        </Suspense>
      )}
    </>
  );
};

const ProjectRoute = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Use selectors to prevent unnecessary re-renders
  const quotes = useStore((state) => state.quotes);
  const updateQuote = useStore((state) => state.updateQuote);
  const addInvoice = useStore((state) => state.addInvoice);
  const updateInvoice = useStore((state) => state.updateInvoice);
  const deleteInvoice = useStore((state) => state.deleteInvoice);
  const createChangeOrder = useStore((state) => state.createChangeOrder);
  const mergePriceList = useStore((state) => state.mergePriceList);
  const companyInfo = useStore((state) => state.companyInfo);
  const addTimeEntry = useStore((state) => state.addTimeEntry);
  const timeEntries = useStore((state) => state.timeEntries);
  const invoices = useStore((state) => state.invoices);

  const quote = quotes.find((q) => q.id === id);
  const resumeParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const resumeInvoiceId = resumeParams.get("resumeInvoiceId");
  const resumeStep = resumeParams.get("resumeStep") as
    | "BUILD"
    | "PREVIEW"
    | "SEND"
    | "CONFIRM"
    | null;
  const resumePreviewPhase = resumeParams.get("resumePreviewPhase") as "DRAFT" | "ISSUED" | null;

  // Fix: Use separate effects and avoid full 'store' dependency
  useEffect(() => {
    if (id) {
      useStore.getState().setActiveQuoteId(id);
    }
  }, [id]);

  useEffect(() => {
    // Redirect to Quote view if NOT Won
    if (quote && quote.status !== QuoteStatus.WON) {
      navigate(`/quote/${quote.id}`, { replace: true });
    }
  }, [quote?.status, quote?.id, navigate]);

  if (!quote)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--app-bg)]">
        <div className="text-center">
          <div className="text-dim text-sm uppercase tracking-wider">Project not found</div>
        </div>
      </div>
    );

  return (
    <ErrorBoundary>
      <Suspense fallback={<RouteLoader />}>
        <div data-testid="project-manager-view">
          <ProjectManager
            quote={quote}
            onUpdateQuote={updateQuote}
            onBack={() => navigate("/")}
            allQuotes={quotes}
            timeEntries={timeEntries}
            invoices={invoices}
            resumeInvoiceManager={
              resumeInvoiceId
                ? {
                    invoiceId: resumeInvoiceId,
                    step: resumeStep ?? undefined,
                    previewPhase: resumePreviewPhase ?? undefined,
                  }
                : undefined
            }
            onAddInvoice={addInvoice}
            onUpdateInvoice={updateInvoice}
            onDeleteInvoice={deleteInvoice}
            onSaveSchedule={(schedule) => updateQuote({ ...quote, schedule })}
            onCreateChangeOrder={(parent) => {
              createChangeOrder(parent);
              navigate("/");
            }}
            onMergePriceList={mergePriceList}
            companyInfo={companyInfo}
            onAddTimeEntry={addTimeEntry}
          />
        </div>
      </Suspense>
    </ErrorBoundary>
  );
};

const DashboardRoute = () => {
  const navigate = useNavigate();
  const [showPriceList, setShowPriceList] = useState(false);

  // Use selectors to prevent unnecessary re-renders
  const quotes = useStore((state) => state.quotes);
  const customers = useStore((state) => state.customers);
  const subcontractors = useStore((state) => state.subcontractors);
  const timeEntries = useStore((state) => state.timeEntries);
  const invoices = useStore((state) => state.invoices);
  const laborRoles = useStore((state) => state.laborRoles);
  const globalSettings = useStore((state) => state.globalSettings);
  const companyInfo = useStore((state) => state.companyInfo);
  const createQuote = useStore((state) => state.createQuote);
  const createChangeOrder = useStore((state) => state.createChangeOrder);
  const updateGlobalSettings = useStore((state) => state.updateGlobalSettings);
  const addCustomer = useStore((state) => state.addCustomer);
  const updateCustomer = useStore((state) => state.updateCustomer);
  const deleteCustomer = useStore((state) => state.deleteCustomer);
  const addSubcontractor = useStore((state) => state.addSubcontractor);
  const updateSubcontractor = useStore((state) => state.updateSubcontractor);
  const deleteSubcontractor = useStore((state) => state.deleteSubcontractor);
  const addInvoice = useStore((state) => state.addInvoice);
  const updateInvoice = useStore((state) => state.updateInvoice);
  const deleteInvoice = useStore((state) => state.deleteInvoice);
  const duplicateQuote = useStore((state) => state.duplicateQuote);
  const updateQuote = useStore((state) => state.updateQuote);
  const priceList = useStore((state) => state.priceList);
  const updatePriceList = useStore((state) => state.updatePriceList);
  const mergePriceList = useStore((state) => state.mergePriceList);
  const activeQuoteId = useStore((state) => state.activeQuoteId);
  const kits = useStore((state) => state.kits);
  const termsTemplates = useStore((state) => state.termsTemplates);

  return (
    <>
      <ErrorBoundary>
        <Suspense fallback={<RouteLoader />}>
          <div data-testid="dashboard-view">
            <Dashboard
              quotes={quotes}
              customers={customers}
              subcontractors={subcontractors}
              timeEntries={timeEntries}
              invoices={invoices}
              onSelectQuote={(id) => {
                const q = quotes.find((q) => q.id === id);
                if (q) {
                  navigate(q.status === QuoteStatus.WON ? `/project/${id}` : `/quote/${id}`);
                }
              }}
              onCreateQuote={(data) => {
                const newId = createQuote(data);
                navigate(data.mode === QuoteMode.SERVICE ? `/project/${newId}` : `/quote/${newId}`);
                return newId;
              }}
              onCreateChangeOrder={(parent) => {
                createChangeOrder(parent);
                const newId = activeQuoteId;
                if (newId) navigate(`/quote/${newId}`);
              }}
              globalSettings={{
                roles: laborRoles,
                markup: globalSettings.markup,
                pst: globalSettings.pst,
                pdfSettings: globalSettings.pdf,
                validityDays: globalSettings.validityDays,
                defaultContingencyPercent: globalSettings.defaultContingencyPercent,
              }}
              companyInfo={companyInfo}
              onUpdateGlobalSettings={updateGlobalSettings}
              onOpenPriceList={() => setShowPriceList(true)}
              onExportData={() => {
                const data = {
                  quotes,
                  timeEntries,
                  invoices,
                  globalLaborRoles: laborRoles,
                  globalSettings,
                  companyInfo,
                  kits,
                  priceList,
                  customers,
                  termsTemplates,
                  version: 4,
                  timestamp: Date.now(),
                };
                const blob = new Blob([JSON.stringify(data, null, 2)], {
                  type: "application/json",
                });
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = `oler_backup_${new Date().toISOString().split("T")[0]}.json`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
              onImportData={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (!confirm("Overwrite current data with backup?")) return;
                const reader = new FileReader();
                reader.onload = (event) => {
                  try {
                    const data = JSON.parse(event.target?.result as string);
                    localStorage.setItem("oler_quotes", JSON.stringify(data.quotes));
                    localStorage.setItem("oler_time_entries", JSON.stringify(data.timeEntries));
                    localStorage.setItem("oler_invoices", JSON.stringify(data.invoices));
                    localStorage.setItem("oler_labor_roles", JSON.stringify(data.globalLaborRoles));
                    localStorage.setItem(
                      "oler_global_settings",
                      JSON.stringify(data.globalSettings)
                    );
                    if (data.companyInfo)
                      localStorage.setItem("oler_company_info", JSON.stringify(data.companyInfo));
                    localStorage.setItem("oler_kits", JSON.stringify(data.kits));
                    localStorage.setItem("oler_price_list", JSON.stringify(data.priceList));
                    localStorage.setItem("oler_customers", JSON.stringify(data.customers));
                    localStorage.setItem(
                      "oler_terms_templates",
                      JSON.stringify(data.termsTemplates)
                    );
                    window.location.reload();
                  } catch (err) {
                    alert("Failed to restore.");
                  }
                };
                reader.readAsText(file);
              }}
              onAddCustomer={addCustomer}
              onUpdateCustomer={updateCustomer}
              onDeleteCustomer={deleteCustomer}
              onAddSub={addSubcontractor}
              onUpdateSub={updateSubcontractor}
              onDeleteSub={deleteSubcontractor}
              onAddInvoice={addInvoice}
              onUpdateInvoice={updateInvoice}
              onDeleteInvoice={deleteInvoice}
              onDuplicateQuote={(id) => {
                duplicateQuote(id);
                // State is updated by duplicateQuote, no reload needed
              }}
              onUpdateQuote={updateQuote}
            />
          </div>
        </Suspense>
      </ErrorBoundary>
      {showPriceList && (
        <Suspense fallback={<RouteLoader />}>
          <PriceListManager
            priceList={priceList}
            onUpdateList={updatePriceList}
            onMergeList={mergePriceList}
            onClose={() => setShowPriceList(false)}
          />
        </Suspense>
      )}
    </>
  );
};

const M365Route = () => {
  const navigate = useNavigate();

  return (
    <ErrorBoundary>
      <Suspense fallback={<RouteLoader />}>
        <div className="flex flex-col h-screen bg-[var(--app-bg)]">
          <Microsoft365Panel onBack={() => navigate("/")} />
        </div>
      </Suspense>
    </ErrorBoundary>
  );
};

// --- LAYOUT & NAVIGATION ---

const TopNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [dashboardView, setDashboardView] = useState<string | null>(() => {
    if (typeof document === "undefined") return null;
    return document.documentElement.dataset.dashboardView ?? null;
  });
  const isField = location.pathname === "/field";
  const isDetailView =
    location.pathname.includes("/quote/") || location.pathname.includes("/project/");
  const isQboManager = location.pathname.startsWith("/quickbooks");
  const isM365View = location.pathname.startsWith("/m365");
  const isScheduleView = location.pathname === "/" && dashboardView === "SCHEDULE";

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleDashboardView = (event: Event) => {
      const nextView = (event as CustomEvent<string | null>).detail ?? null;
      setDashboardView(nextView);
    };
    window.addEventListener("dashboard-view-change", handleDashboardView as EventListener);
    return () =>
      window.removeEventListener("dashboard-view-change", handleDashboardView as EventListener);
  }, []);

  if (isDetailView || isQboManager || isM365View || isScheduleView) return null;

  return (
    <div className="bg-[var(--bezel-bg)] border-b border-[color:var(--border)] px-4 py-2 flex justify-center sticky top-0 z-20">
      <div className="flex bg-[var(--surface-bg)] rounded-lg p-1 border border-[color:var(--border)]">
        <button
          onClick={() => navigate("/")}
          className={`
            flex items-center px-4 py-2 rounded-md text-sm font-bold uppercase tracking-wider
            transition-all duration-fast
            ${
              !isField
                ? "bg-oler-blue/20 text-oler-blue-light border border-oler-blue/30 shadow-glow-primary"
                : "text-sub hover:text-[color:var(--text)] border border-transparent"
            }
          `}
        >
          <LayoutGrid className="w-4 h-4 mr-2" /> Office
        </button>
        <button
          onClick={() => navigate("/field")}
          className={`
             flex items-center px-4 py-2 rounded-md text-sm font-bold uppercase tracking-wider
             transition-all duration-fast
             ${
               isField
                 ? "bg-oler-blue/20 text-oler-blue-light border border-oler-blue/30 shadow-glow-primary"
                 : "text-sub hover:text-[color:var(--text)] border border-transparent"
             }
           `}
        >
          <Clock className="w-4 h-4 mr-2" /> Field
        </button>
      </div>
    </div>
  );
};

const AppContent = () => {
  const loading = useStore((state) => state.loading);
  const loadData = useStore((state) => state.loadData);
  const theme = useStore((state) => state.theme);
  const quotes = useStore((state) => state.quotes);
  const laborRoles = useStore((state) => state.laborRoles);
  const timeEntries = useStore((state) => state.timeEntries);
  const updateQBOConfig = useStore((state) => state.updateQBOConfig);
  const addTimeEntry = useStore((state) => state.addTimeEntry);
  const deleteTimeEntry = useStore((state) => state.deleteTimeEntry);
  const location = useLocation();
  const navigate = useNavigate();

  // Sync theme to DOM - using data-theme attribute for CSS variable switching
  useEffect(() => {
    // Default to DARK theme (industrial SCADA default)
    const themeMode = theme === "light" ? "LIGHT" : "DARK";
    document.documentElement.dataset.theme = themeMode;

    // Also set class for Tailwind dark: classes that may remain
    document.documentElement.classList.toggle("dark", theme !== "light");
  }, [theme]);

  // Set initial theme on mount
  useEffect(() => {
    const themeMode = theme === "light" ? "LIGHT" : "DARK";
    document.documentElement.dataset.theme = themeMode;
    document.documentElement.classList.toggle("dark", theme !== "light");
  }, []);

  useEffect(() => {
    const handleOAuthCallback = async () => {
      const hash = window.location.hash || "";
      const query = hash.includes("?") ? hash.split("?")[1] : "";
      const params = new URLSearchParams(query);
      const qboConnected = params.get("qbo_connected");
      const qboError = params.get("qbo_error");
      const realmId = params.get("realmId");
      const outlookParams = new URLSearchParams(window.location.search);
      const outlookConnected = outlookParams.get("outlook_connected");
      const outlookError = outlookParams.get("outlook_error");

      if (qboConnected === "1" && realmId) {
        await updateQBOConfig({ realmId });
        window.history.replaceState({}, "", window.location.pathname + hash.split("?")[0]);
      }

      if (qboError) {
        alert(`QuickBooks connection error: ${qboError}`);
        window.history.replaceState({}, "", window.location.pathname + hash.split("?")[0]);
      }

      if (outlookError) {
        alert(`Microsoft 365 connection error: ${outlookError}`);
        window.history.replaceState({}, "", window.location.pathname + hash);
      }

      const oauthSucceeded = (qboConnected === "1" && realmId) || outlookConnected === "true";
      if (oauthSucceeded) {
        if (outlookConnected === "true") {
          window.history.replaceState({}, "", window.location.pathname + hash);
        }
        const payload = consumeInvoiceManagerResumePayload();
        if (payload?.quoteId) {
          const resumeQuery = new URLSearchParams();
          resumeQuery.set("resumeInvoiceId", payload.invoiceId);
          resumeQuery.set("resumeStep", payload.step);
          if (payload.previewPhase) resumeQuery.set("resumePreviewPhase", payload.previewPhase);
          navigate(`/project/${payload.quoteId}?${resumeQuery.toString()}`);
        }
      }
    };

    handleOAuthCallback();
  }, [location.key, navigate, updateQBOConfig]);

  useEffect(() => {
    loadData();

    // Initialize performance monitoring if enabled
    if (isFeatureEnabled("enablePerformanceMonitoring")) {
      performanceMonitor.init();
      performanceMonitor.trackBundleSize();
      performanceMonitor.trackStorageUsage();
    }

    // Check for supplier documents in email (non-blocking, runs after initial load)
    // Only run once per session
    const sessionKey = "oler_supplier_doc_check_session";
    const lastSession = sessionStorage.getItem(sessionKey);
    if (!lastSession) {
      sessionStorage.setItem(sessionKey, Date.now().toString());

      // Run after a short delay to not block initial load
      setTimeout(async () => {
        try {
          const { checkForSupplierDocuments } =
            await import("./services/supplierDocuments/emailMonitor");
          // Get quotes from store for linking
          const currentQuotes = useStore.getState().quotes;
          // Settings are loaded inside checkForSupplierDocuments from storage
          await checkForSupplierDocuments({ quotes: currentQuotes });
        } catch (error) {
          console.error("Error checking for supplier documents:", error);
          // Silently fail - don't interrupt user experience
        }
      }, 2000); // Wait 2 seconds after app load
    }
  }, [loadData]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--app-bg)] flex-col">
        <div className="relative">
          <div className="w-16 h-16 rounded-full border-4 border-[color:var(--border)]" />
          <div className="absolute inset-0 w-16 h-16 rounded-full border-4 border-oler-blue border-t-transparent animate-spin" />
        </div>
        <h2 className="text-lg font-bold text-[color:var(--text)] mt-6 tracking-wider uppercase">
          Loading Oler Quote System...
        </h2>
        <p className="text-dim text-sm mt-2">Syncing with secure database</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--app-bg)] text-[color:var(--text)] font-sans flex flex-col transition-colors">
      <TopNavigation />
      <AuthWarningBanner />
      <div className="flex-1 flex flex-col">
        <ErrorBoundary>
          <Suspense fallback={<RouteLoader />}>
            <Routes>
              <Route path="/" element={<DashboardRoute />} />
              <Route
                path="/field"
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<RouteLoader />}>
                      <TimeTracker
                        quotes={quotes}
                        entries={timeEntries}
                        roles={laborRoles}
                        onAddEntry={addTimeEntry}
                        onDeleteEntry={deleteTimeEntry}
                      />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
              <Route path="/quote/:id" element={<QuoteRoute />} />
              <Route path="/project/:id" element={<ProjectRoute />} />
              <Route
                path="/quickbooks"
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<RouteLoader />}>
                      <QuickBooksManager />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
              <Route path="/m365" element={<M365Route />} />
              <Route
                path="/eula"
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<RouteLoader />}>
                      <EULAPage />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/privacy"
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<RouteLoader />}>
                      <PrivacyPolicyPage />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/marketing"
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<RouteLoader />}>
                      <Marketing />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/marketing/campaigns"
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<RouteLoader />}>
                      <Marketing />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/marketing/campaigns/:id"
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<RouteLoader />}>
                      <CampaignDetail />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/marketing/contacts"
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<RouteLoader />}>
                      <Marketing />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/marketing/templates"
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<RouteLoader />}>
                      <Marketing />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/scout"
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<RouteLoader />}>
                      <Scout />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/scout/leads"
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<RouteLoader />}>
                      <Scout />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/scout/test"
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<RouteLoader />}>
                      <ScoutTest />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/scout/analytics"
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<RouteLoader />}>
                      <ScoutAnalytics />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/herald"
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<RouteLoader />}>
                      <HeraldInbox />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/settings/email-templates"
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<RouteLoader />}>
                      <EmailTemplates />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/settings/email-queue"
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<RouteLoader />}>
                      <EmailQueue />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/settings/forge-templates"
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<RouteLoader />}>
                      <ForgeTemplates />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/forge/documents"
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<RouteLoader />}>
                      <ForgeDocuments />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
              {/* Agent Inbox */}
              <Route
                path="/inbox"
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<RouteLoader />}>
                      <AgentInbox />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
              {/* Convenience aliases for direct URL access */}
              <Route
                path="/email-templates"
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<RouteLoader />}>
                      <EmailTemplates />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/email-queue"
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<RouteLoader />}>
                      <EmailQueue />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </div>

      {/* Global Feedback Button - appears on all pages */}
      <FeedbackButton position="bottom-right" />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <HashRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AppContent />
    </HashRouter>
  );
};

export default App;
